import { map } from 'rxjs';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { DynamicRouteTitle } from './routeNode';

export type TranslateTitle = (i18nLocale: I18nLocale) => string;

/**
 * Builds a route title from the current locale.
 *
 * Use this for titles composed of several keys or of interpolated values.
 * For a single translation key, use {@link i18nTitle}.
 *
 * ```ts
 * title: dynamicI18nTitle(
 *   i18nLocale =>
 *     `${t(i18nLocale, 'report:builder.breadcrumb.untitledReport', 'Untitled report')} - ${t(i18nLocale, 'report:navigation.builderTitle', 'Report Builder')}`
 * ),
 * ```
 */
export const dynamicI18nTitle =
  (translate: TranslateTitle): (() => DynamicRouteTitle) =>
  async () =>
    (await import('streams/i18nLocale$')).i18nLocale$.pipe(map(translate));

export const translateTitle =
  (key: string, defaultText: string): TranslateTitle =>
  i18nLocale =>
    t(i18nLocale, key, defaultText);

/**
 * A route title translated from a single key.
 *
 * ```ts
 * title: i18nTitle('report:navigation.builderTitle', 'Report Builder'),
 * ```
 */
export const i18nTitle = (
  key: string,
  defaultText: string
): (() => DynamicRouteTitle) =>
  dynamicI18nTitle(translateTitle(key, defaultText));
